import {
    Button, Card, Col, ConfigProvider, DatePicker, Empty, Form, Input, Modal, Row,
    Select, Space, Spin, Table, Tag, Tooltip, Typography
} from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { createRef, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import {
    PrinterOutlined, ReloadOutlined, CreditCardOutlined, ExclamationCircleFilled,
    FileDoneOutlined, BankOutlined, CheckCircleTwoTone, EditOutlined, CalendarOutlined, StopOutlined
} from "@ant-design/icons";
import { rightsService } from "../../services/rightsService";

import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { frenchDate } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";
import { StatCard } from "../../components/statistics/StatCard";
import { COLORS } from "../../components/ui/colors";
import GeneratedBordereauCommissions from "../caisse/commissions/GeneratedBordereauCommissions";
import { getCompagnieLogo } from "../../hooks/useFilesHooks";
import entete from "../../assets/img/entete.jpg";
import { DocumentFooter } from "../../components/docs/DocumentFooter";

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

// Date de référence d'un bordereau de commission pour le filtre « Période » de l'état :
// la date de règlement de la commission si elle est réglée, sinon la date d'encaissement la
// plus récente parmi ses règlements (période couverte), à défaut sa date de création.
const bordereauDateRef = (b) => {
    if (b?.dateReglement) return b.dateReglement;
    const dates = (b?.reglements || []).map((r) => r?.dateReglement).filter(Boolean).sort();
    return dates[dates.length - 1] || b?.createdAt || null;
};

// Bénéficiaire d'un bordereau : l'agence rattachée si elle est résolue, sinon REPLI sur
// l'observation (« Bénéficiaire : NOM (TYPE - CODE) » figé à la génération) — couvre les cas
// intermédiaire / agence historisée où bordereau.agence est nul.
const beneficiaireNomOf = (b) =>
    b?.agence?.nom
    || (/Bénéficiaire\s*:\s*(.+?)\s*\(/.exec(b?.observation || "")?.[1]?.trim())
    || null;

const MODE_COLORS = {
    ESPECES: "blue", RISTOURNE: "green", BANQUE: "gold",
    VIREMENT: "geekblue", MOBILE_MONEY: "magenta", COMPENSATION: "purple",
};

const isPaye = (b) => !!((b?.modePaiement && b.modePaiement !== "-") || b?.modePaiementCommission);
// Un bordereau de commission est VALIDÉ (flag posé à l'étape Valider, HLA108) et non encore payé.
const isValide = (b) => b?.valide === true;
// Statut dérivé (précédence Payé > Validé > À valider) → { label, color } pour le Tag.
const statutOf = (b) => {
    if (isPaye(b)) return { label: `Payé${b?.modePaiement ? ` · ${b.modePaiement}` : ""}`, color: MODE_COLORS[b?.modePaiement] || "green" };
    if (isValide(b)) return { label: "Validé", color: "blue" };
    return { label: "À valider", color: "orange" };
};

const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

// Compagnie (organisme) d'un bordereau : déduite des règlements (contrat.organismeCode).
const organismeCodeOf = (b) => {
    const r = (b?.reglements || []).find((x) => x?.contrat?.organismeCode);
    return r?.contrat?.organismeCode || null;
};

// Mappe les règlements d'un bordereau persisté vers le format « plat » attendu par
// GeneratedBordereauCommissions (champs nichés sous reglement.contrat).
// DÉDUP par codeId de règlement : la sérialisation backend du bordereau (chargement JOIN FETCH
// contrat→engins) renvoie le MÊME règlement répété une fois par engin (produit cartésien Hibernate).
// Les données en base sont correctes (1 lien par règlement) ; on n'affiche donc qu'une occurrence,
// ce qui corrige à la fois le nombre de lignes ET le total (calculé depuis emissions).
const mapBordereauToEmissions = (bord) => {
    const seen = new Set();
    return (bord?.reglements || [])
        .filter((reg) => {
            const k = reg?.codeId;
            if (!k) return true;
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        })
        .map((reg) => ({
    codeId: reg.codeId,
    numeroPolice: reg.contrat?.numeroPoliceP || reg.contrat?.numeroPolice,
    avenant: reg.avenantCode || reg.avenant,
    nomSouscripteur: reg.contrat?.souscripteur?.nom,
    prenomSouscripteur: reg.contrat?.souscripteur?.prenom,
    dateEffet: reg.contrat?.dateEffet,
    dateEcheance: reg.contrat?.dateEcheance,
    dateReglement: reg.dateReglement,
    categorie: reg.contrat?.categorie,
    montant: reg.montant,
    accessoires: reg.accessoires,
    taxes: reg.taxes,
    tauxCommission: reg.tauxCommission,
    commission: reg.commission,
    tauxRetenue: reg.tauxRetenue,
        }));
};

/** Aperçu imprimable d'un bordereau de commission (réutilise le document refondu). */
const PrintableBordereau = ({ bordereau }) => {
    const ref = createRef();
    const handlePrint = useReactToPrint({ content: () => ref.current });
    const emissions = mapBordereauToEmissions(bordereau);

    // Logo de la compagnie (déduit des règlements) affiché à côté de l'en-tête du Pool.
    const [logoCompagnie, setLogoCompagnie] = useState(null);
    useEffect(() => {
        const code = organismeCodeOf(bordereau);
        if (!code) return undefined;
        let url;
        getCompagnieLogo(code)
            .then(async (response) => {
                const bytes = await response.arrayBuffer();
                const blob = new Blob([bytes], { type: "image/png" });
                url = URL.createObjectURL(blob);
                setLogoCompagnie(url);
            })
            .catch((err) => console.log(err));
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [bordereau]);

    return (
        <Card style={{ borderRadius: 14, marginBottom: 8, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
            bodyStyle={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Space wrap>
                    <Tag color="cyan" style={{ borderRadius: 6 }}><BankOutlined /> {beneficiaireNomOf(bordereau) || "—"}</Tag>
                    <Tag color="orange" style={{ borderRadius: 6 }}>N° {bordereau?.numeroBordereau || "—"}</Tag>
                    <Text type="secondary">· {emissions.length} commission(s)</Text>
                    {isPaye(bordereau)
                        ? <Tag color="green" style={{ borderRadius: 6 }}>Payé</Tag>
                        : <Tag style={{ borderRadius: 6 }}>Non payé</Tag>}
                </Space>
                <Button icon={<PrinterOutlined />} onClick={handlePrint} type="primary"
                    style={{ background: COLORS.gradient, border: "none", borderRadius: 8 }}>
                    Imprimer
                </Button>
            </div>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 8, overflowX: "auto" }}>
                <div ref={ref}>
                    <GeneratedBordereauCommissions
                        emissions={emissions}
                        logoCompagnie={logoCompagnie}
                        beneficiaireNom={beneficiaireNomOf(bordereau)}
                        numeroBordereau={bordereau?.numeroBordereau}
                        tauxRetenue={bordereau?.tauxRetenue}
                        montantRetenue={bordereau?.montantRetenue}
                        montantCommissionNette={bordereau?.montantCommissionNette}
                        periodeDebut={bordereau?.periodeDebut}
                        periodeFin={bordereau?.periodeFin}
                        modePaiement={bordereau?.modePaiementCommissions || bordereau?.modePaiement}
                        numeroPaiement={bordereau?.numeroPaiementCommissions || bordereau?.numeroPaiement}
                        banque={bordereau?.banqueCommissions || bordereau?.banque}
                        datePaiement={bordereau?.commissionPayeLe || bordereau?.dateReglement}
                        commentaireEncaissement={bordereau?.commentaireEncaissement}
                    />
                </div>
            </div>
        </Card>
    );
};

/**
 * État imprimable des bordereaux de commissions (payées / non payées / sur une période).
 * Document destiné à l'impression — réf. transmise via `forwardRef` pour react-to-print.
 */
const etatPageStyle = {
    width: "210mm",
    minHeight: "297mm",
    padding: "12mm",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    color: "#000",
    background: "#fff",
};

const EtatCommissions = forwardRef(({ bordereaux, periodeLabel, statutLabel }, ref) => {
    const total = bordereaux.reduce((s, b) => s + (b.montantTotal || 0), 0);
    const cellH = { border: "1px solid #ccc", padding: "6px 8px", fontSize: 12, background: "#f5f5f5", textAlign: "left" };
    const cell = { border: "1px solid #ccc", padding: "6px 8px", fontSize: 12 };
    return (
        <div ref={ref}>
            <div style={etatPageStyle}>
                {/* En-tête identique aux états de reversement (en-tête Pool). */}
                <div style={{ flexShrink: 0 }}>
                    <img src={entete} alt="" style={{ width: "100%", maxHeight: 110, objectFit: "contain" }} />
                    <h3 style={{ textAlign: "center", color: "#272366", margin: "8px 0 2px" }}>
                        ÉTAT DES BORDEREAUX DE COMMISSIONS
                    </h3>
                    <div style={{ textAlign: "center", fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{statutLabel}</div>
                    {periodeLabel ? <div style={{ textAlign: "center", fontSize: 12, marginBottom: 8 }}>{periodeLabel}</div> : null}
                </div>

                <div style={{ flex: 1, marginTop: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={cellH}>N°</th>
                                <th style={cellH}>N° Bordereau</th>
                                <th style={cellH}>Apporteur</th>
                                <th style={cellH}>Date paiement</th>
                                <th style={{ ...cellH, textAlign: "right" }}>Montant</th>
                                <th style={cellH}>Statut</th>
                                <th style={cellH}>Mode</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bordereaux.map((b, i) => (
                                <tr key={b.codeId || i}>
                                    <td style={cell}>{i + 1}</td>
                                    <td style={cell}>{b.numeroBordereau || "—"}</td>
                                    <td style={cell}>{b.agence?.nom || "—"}</td>
                                    <td style={cell}>{isPaye(b) && b.dateReglement ? frenchDate(b.dateReglement) : "-"}</td>
                                    <td style={{ ...cell, textAlign: "right" }}>{displayNumbers(Math.trunc(b.montantTotal || 0))}</td>
                                    <td style={cell}>{isPaye(b) ? "Payé" : "Non payé"}</td>
                                    <td style={cell}>{isPaye(b) ? (b.modePaiement || "-") : "-"}</td>
                                </tr>
                            ))}
                            {bordereaux.length === 0 ? (
                                <tr><td style={cell} colSpan={7}>Aucun bordereau pour ces critères.</td></tr>
                            ) : null}
                        </tbody>
                        <tfoot>
                            <tr style={{ fontWeight: 700, background: "#e2efda" }}>
                                <td style={{ ...cellH, textAlign: "right", background: "#e2efda" }} colSpan={4}>TOTAL</td>
                                <td style={{ ...cellH, textAlign: "right", background: "#e2efda" }}>{displayNumbers(Math.trunc(total))}</td>
                                <td style={{ ...cellH, background: "#e2efda" }} colSpan={2}>{bordereaux.length} bordereau(x)</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div style={{ flexShrink: 0, marginTop: "6mm" }}>
                    <DocumentFooter />
                </div>
            </div>
        </div>
    );
});
EtatCommissions.displayName = "EtatCommissions";

const Bordereaux = () => {
    const [allDatas, setAllDatas] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [search, setSearch] = useState("");
    const [statutFilter, setStatutFilter] = useState("TOUS");
    // Map code organisme -> raison sociale (pour le tag compagnie, vue Pool)
    const [compagnies, setCompagnies] = useState({});

    const [banques, setBanques] = useState([]);
    const [bordereauToPay, setBordereauToPay] = useState(null);

    const [openModalBorderau, setOpenModalBorderau] = useState(false);
    const [modalModePaiement, setModalModePaiement] = useState(false);
    // true = correction du mode de règlement d'une commission déjà réglée ;
    // false = règlement initial.
    const [editMode, setEditMode] = useState(false);
    const [commentaireEncaissement, setCommentaireEncaissement] = useState("");
    const [modePaiement, setModePaiement] = useState("");
    const [numeroPaiement, setNumeroPaiement] = useState("P");
    const [banque, setBanque] = useState("BANQUE");
    const [modePaiementForm] = Form.useForm();
    // Période de chargement (création des bordereaux) — borne le volume (règlements imbriqués).
    // Défaut : année en cours.
    const [periode, setPeriode] = useState([dayjs().startOf("year"), dayjs()]);

    // Modification EXCEPTIONNELLE de la période affichée (même habilitation que l'annulation : HLA101).
    const peutModifierPeriode = rightsService("HLA101");
    // Validation d'un bordereau (HLA108) et recalcul des commissions (HCO020).
    const peutValider = rightsService("HLA108");
    const peutRecalculer = rightsService("HCO020");
    // Recalcul EN LOT de tous les bordereaux non payés (un clic).
    const [recalculLotEnCours, setRecalculLotEnCours] = useState(false);
    const isSiege = LOGGED_USER?.agence?.typeAgence === "SIEGE";
    const [modalPeriode, setModalPeriode] = useState(false);
    const [bordereauPeriode, setBordereauPeriode] = useState(null);
    const [periodeEdit, setPeriodeEdit] = useState([null, null]);
    const [periodeEnCours, setPeriodeEnCours] = useState(false);

    // ── Annulation d'un bordereau de commission NON payé (HLA101) ──
    const peutAnnuler = rightsService("HLA101");
    const [modalAnnuler, setModalAnnuler] = useState(false);
    const [bordereauAAnnuler, setBordereauAAnnuler] = useState(null);
    const [motifAnnulation, setMotifAnnulation] = useState("");
    const [annulerEnCours, setAnnulerEnCours] = useState(false);

    // ── État des commissions (payées / non payées / sur une période) ──
    const [showEtatModal, setShowEtatModal] = useState(false);
    const [etatStatut, setEtatStatut] = useState("PAYE");        // PAYE | NON_PAYE | TOUS
    const [etatPeriode, setEtatPeriode] = useState(null);        // [dayjs, dayjs] | null
    const etatPrintRef = useRef();
    const handlePrintEtat = useReactToPrint({ content: () => etatPrintRef.current });

    const fetchData = () => {
        setLoadingData(true);
        const startDate = periode && periode[0] ? periode[0].format("YYYY-MM-DD") : null;
        const endDate = periode && periode[1] ? periode[1].format("YYYY-MM-DD") : null;
        axiosInstance.get(`/api/bordereau/all/commissions`, {
            params: { page: 0, size: 5000, search: "", startDate, endDate, compagnie: "" },
        })
            .then((res) => { setAllDatas(res.data || []); setLoadingData(false); })
            .catch(() => setLoadingData(false));
    };

    // Recharge à chaque changement de période (et au montage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, [periode]);

    useEffect(() => {
        axiosInstance.get(`/api/parametre/type/BANQUE`)
            .then((res) => setBanques(res.data || []))
            .catch((err) => console.log(err));
        // Pool : on charge les compagnies pour afficher leur raison sociale dans le tag.
        if (isPool) {
            axiosInstance.get(`/api/organisme/assurance`)
                .then((res) => {
                    const map = {};
                    (res.data || []).forEach((o) => { if (o?.code) map[o.code] = o.raisonSociale || o.code; });
                    setCompagnies(map);
                })
                .catch((err) => console.log(err));
        }
    }, []);

    const compagnieLabelOf = (b) => {
        const code = organismeCodeOf(b);
        if (!code) return null;
        return compagnies[code] || code;
    };

    // ── Filtrage (recherche apporteur / n° bordereau + statut) ──
    const filteredDatas = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allDatas.filter((b) => {
            const matchSearch = !q
                || `${b.agence?.nom || ""}`.toLowerCase().includes(q)
                || `${b.numeroBordereau || ""}`.toLowerCase().includes(q);
            const matchStatut = statutFilter === "TOUS"
                || (statutFilter === "PAYE" && isPaye(b))
                || (statutFilter === "NON_PAYE" && !isPaye(b))
                || (statutFilter === "A_VALIDER" && !isPaye(b) && !isValide(b))
                || (statutFilter === "VALIDE" && !isPaye(b) && isValide(b));
            return matchSearch && matchStatut;
        });
    }, [allDatas, search, statutFilter]);

    // ── Indicateurs ──
    const stats = useMemo(() => {
        const montantTotal = filteredDatas.reduce((s, b) => s + (b.montantTotal || 0), 0);
        const nbPayes = filteredDatas.filter(isPaye).length;
        const nbValides = filteredDatas.filter((b) => !isPaye(b) && isValide(b)).length;
        const nbAValider = filteredDatas.filter((b) => !isPaye(b) && !isValide(b)).length;
        return { montantTotal, nbPayes, nbValides, nbAValider };
    }, [filteredDatas]);

    // Liste des bordereaux retenus pour l'état, selon le statut et la période choisis.
    const etatDatas = useMemo(() => {
        const start = etatPeriode?.[0] ? etatPeriode[0].startOf("day") : null;
        const end = etatPeriode?.[1] ? etatPeriode[1].endOf("day") : null;
        return allDatas.filter((b) => {
            const matchStatut = etatStatut === "TOUS"
                || (etatStatut === "PAYE" && isPaye(b))
                || (etatStatut === "NON_PAYE" && !isPaye(b));
            if (!matchStatut) return false;
            if (!start || !end) return true;
            const ref = bordereauDateRef(b);
            if (!ref) return false;
            const d = dayjs(ref);
            return (d.isSame(start) || d.isAfter(start)) && (d.isSame(end) || d.isBefore(end));
        });
    }, [allDatas, etatStatut, etatPeriode]);

    const etatStatutLabel = etatStatut === "PAYE" ? "Commissions payées"
        : etatStatut === "NON_PAYE" ? "Commissions non payées" : "Toutes les commissions";
    const etatPeriodeLabel = (etatPeriode?.[0] && etatPeriode?.[1])
        ? `PÉRIODE DU ${etatPeriode[0].format("DD/MM/YYYY")} AU ${etatPeriode[1].format("DD/MM/YYYY")}`
        : "";

    const apercu = (bord) => {
        if ((bord?.reglements || []).length === 0) {
            toast.warn("Ce bordereau ne contient aucune commission");
            return;
        }
        setBordereauToPay(bord);
        setOpenModalBorderau(true);
    };

    const encaisserBordereau = (bord) => {
        setEditMode(false);
        setBordereauToPay(bord);
        setModePaiement("");
        setNumeroPaiement("P");
        setBanque("BANQUE");
        setCommentaireEncaissement("");
        modePaiementForm.resetFields();
        setModalModePaiement(true);
    };

    // Correction du mode de règlement d'une commission déjà réglée : on pré-remplit
    // la modale avec les valeurs enregistrées.
    const modifierModePaiement = (bord) => {
        setEditMode(true);
        setBordereauToPay(bord);
        setModePaiement(bord.modePaiement || "");
        setNumeroPaiement(bord.numeroPaiement || "");
        setBanque(bord.banque || "BANQUE");
        modePaiementForm.setFieldsValue({
            modePaiement: bord.modePaiement || undefined,
            numeroPaiement: bord.numeroPaiement || undefined,
            banque: bord.banque || undefined,
        });
        setModalModePaiement(true);
    };

    const alertReglementBordereau = (bd) => {
        if (!modePaiement) { toast.warn("Sélectionnez un mode de paiement"); return; }
        confirm({
            title: "Êtes-vous sûr de vouloir régler ce bordereau ?",
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées !",
            okText: "Oui, régler", okType: "danger", cancelText: "Annuler",
            onOk() { reglementBordereau(bd); },
        });
    };

    const alertModifierModePaiement = (bd) => {
        if (!modePaiement) { toast.warn("Sélectionnez un mode de paiement"); return; }
        confirm({
            title: "Modifier le mode de règlement de cette commission ?",
            icon: <ExclamationCircleFilled />,
            content: "Cette correction est historisée.",
            okText: "Oui, modifier", okType: "danger", cancelText: "Annuler",
            onOk() { enregistrerModePaiement(bd); },
        });
    };

    // Champs cohérents selon le mode : pas de n° / banque pour les espèces.
    const payloadModePaiement = (bd) => ({
        codeId: bd.codeId,
        modePaiement,
        numeroPaiement: modePaiement === "ESPECES" ? null : numeroPaiement,
        banque: modePaiement === "BANQUE" ? banque : null,
        commentaireEncaissement: commentaireEncaissement && commentaireEncaissement.trim()
            ? commentaireEncaissement.trim() : undefined,
    });

    const reglementBordereau = (bd) => {
        axiosInstance.put(`/api/bordereau/encaisser`, payloadModePaiement(bd))
            .then(() => {
                toast.success("Règlement effectué avec succès !");
                setModalModePaiement(false);
                fetchData();
            })
            .catch((err) => { console.log(err); toast.error("Erreur lors du règlement"); });
    };

    const enregistrerModePaiement = (bd) => {
        axiosInstance.put(`/api/bordereau/mode-paiement`, payloadModePaiement(bd))
            .then(() => {
                toast.success("Mode de règlement modifié avec succès !");
                setModalModePaiement(false);
                fetchData();
            })
            .catch((err) => { console.log(err); toast.error("Erreur lors de la modification"); });
    };

    // ── Validation d'un bordereau (HLA108), préalable au paiement ──
    const alertValider = (bord) => {
        confirm({
            title: "Valider ce bordereau de commission ?",
            icon: <ExclamationCircleFilled />,
            content: "Une fois validé, il pourra être payé. Un recalcul ultérieur le remettra « à valider ».",
            okText: "Oui, valider", cancelText: "Annuler",
            onOk() {
                return axiosInstance.put(`/api/bordereau/valider/${bord.codeId}`)
                    .then(() => { toast.success("Bordereau validé."); fetchData(); })
                    .catch((err) => toast.error(err?.response?.data?.message || "Erreur lors de la validation"));
            },
        });
    };

    // ── Recalcul des commissions + retenue d'un bordereau non payé (HCO020) ──
    const alertRecalculer = (bord) => {
        confirm({
            title: "Recalculer les commissions de ce bordereau ?",
            icon: <ExclamationCircleFilled />,
            content: "Les commissions et la retenue fiscale seront recalculées au taux courant. Le bordereau devra ensuite être re-validé.",
            okText: "Oui, recalculer", okType: "danger", cancelText: "Annuler",
            onOk() {
                return axiosInstance.post(`/api/bordereau/${bord.codeId}/recalculer`)
                    .then((res) => {
                        const d = res.data || {};
                        const av = Math.trunc(d.ancienneCommissionNette || 0);
                        const ap = Math.trunc(d.nouvelleCommissionNette || 0);
                        toast.success(`Recalcul effectué — commission nette : ${displayNumbers(av)} → ${displayNumbers(ap)} F. À re-valider.`);
                        fetchData();
                    })
                    .catch((err) => toast.error(err?.response?.data?.message || "Erreur lors du recalcul"));
            },
        });
    };

    // ── Recalcul EN LOT de TOUS les bordereaux non payés (HCO020) ──
    // Réutilise l'endpoint par bordereau (transaction indépendante par appel) en séquentiel :
    // partiel-tolérant (un bordereau déjà payé/en erreur est compté sans bloquer les autres).
    const alertRecalculerLot = () => {
        const cibles = allDatas.filter((b) => !isPaye(b) && b.codeId);
        if (!cibles.length) { toast.info("Aucun bordereau non payé à recalculer."); return; }
        confirm({
            title: `Recalculer les ${cibles.length} bordereau(x) non payé(s) ?`,
            icon: <ExclamationCircleFilled />,
            content: "Les commissions et la retenue fiscale de TOUS les bordereaux non payés seront recalculées au taux courant. Ils devront ensuite être re-validés.",
            okText: "Oui, tout recalculer", okType: "danger", cancelText: "Annuler",
            onOk() { return recalculerLot(cibles); },
        });
    };
    const recalculerLot = async (cibles) => {
        setRecalculLotEnCours(true);
        let ok = 0, echecs = 0;
        for (const b of cibles) {
            try {
                await axiosInstance.post(`/api/bordereau/${b.codeId}/recalculer`);
                ok += 1;
            } catch (e) {
                echecs += 1;
            }
        }
        setRecalculLotEnCours(false);
        if (echecs === 0) toast.success(`${ok} bordereau(x) recalculé(s). À re-valider.`);
        else toast.warn(`${ok} recalculé(s), ${echecs} en échec (déjà payé ou erreur).`);
        fetchData();
    };

    // ── Modification EXCEPTIONNELLE de la période affichée (HLA101) ────────
    const ouvrirPeriode = (bd) => {
        setBordereauPeriode(bd);
        setPeriodeEdit([
            bd?.periodeDebut ? dayjs(bd.periodeDebut) : null,
            bd?.periodeFin ? dayjs(bd.periodeFin) : null,
        ]);
        setModalPeriode(true);
    };

    const confirmerPeriode = () => {
        setPeriodeEnCours(true);
        axiosInstance
            .put(`/api/bordereau/${bordereauPeriode?.codeId}/periode`, null, {
                params: {
                    startDate: periodeEdit && periodeEdit[0] ? periodeEdit[0].format("YYYY-MM-DD") : null,
                    endDate: periodeEdit && periodeEdit[1] ? periodeEdit[1].format("YYYY-MM-DD") : null,
                },
            })
            .then(() => {
                toast.success("Période du bordereau modifiée.");
                setModalPeriode(false);
                setBordereauPeriode(null);
                fetchData();
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || err?.response?.data?.error
                    || "Erreur lors de la modification de la période";
                toast.error(msg);
            })
            .finally(() => setPeriodeEnCours(false));
    };

    // ── Annulation d'un bordereau de commission NON payé (HLA101) ──
    const ouvrirAnnulation = (bd) => {
        setBordereauAAnnuler(bd);
        setMotifAnnulation("");
        setModalAnnuler(true);
    };

    const confirmerAnnulation = () => {
        if (!motifAnnulation || !motifAnnulation.trim()) {
            toast.error("Le motif d'annulation est obligatoire.");
            return;
        }
        setAnnulerEnCours(true);
        axiosInstance
            .put(`/api/bordereau/annuler/${bordereauAAnnuler?.codeId}`, { motif: motifAnnulation.trim() })
            .then(() => {
                toast.success("Bordereau annulé. Les commissions concernées sont de nouveau disponibles.");
                setModalAnnuler(false);
                setBordereauAAnnuler(null);
                setMotifAnnulation("");
                fetchData();
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || err?.response?.data?.error
                    || "Erreur lors de l'annulation du bordereau";
                toast.error(msg);
            })
            .finally(() => setAnnulerEnCours(false));
    };

    const columns = [
        { title: "N° bordereau", dataIndex: "numeroBordereau", key: "numeroBordereau", render: (v) => <Text strong>{v}</Text> },
        // Colonne Compagnie : uniquement pour le Pool (qui voit toutes les compagnies).
        ...(isPool ? [{
            title: "Compagnie", key: "compagnie",
            render: (_, r) => {
                const label = compagnieLabelOf(r);
                return label ? <Tag color="purple" style={{ borderRadius: 6 }}><BankOutlined /> {label}</Tag> : <Tag>—</Tag>;
            },
        }] : []),
        {
            title: "Apporteur", dataIndex: "agence", key: "agence",
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: 13 }}>{beneficiaireNomOf(r) || "—"}</Text>
                    {r.agence?.codeAgence ? <Tag color="cyan" style={{ borderRadius: 6, fontSize: 11 }}><BankOutlined /> {r.agence.codeAgence}</Tag> : null}
                </Space>
            ),
        },
        {
            title: "Montant", dataIndex: "montantTotal", key: "montantTotal",
            align: "right", render: (_, r) => <Text strong>{displayNumbers(Math.trunc(r.montantTotal || 0))}</Text>,
        },
        {
            title: "Statut", key: "statut",
            render: (_, r) => { const s = statutOf(r); return <Tag color={s.color}>{s.label}</Tag>; },
        },
        { title: "Date paiement commission", dataIndex: "dateReglement", key: "dateReglement", render: (_, r) => (isPaye(r) && r.dateReglement ? frenchDate(r.dateReglement) : "-") },
        {
            title: "", key: "actions", width: 110,
            render: (_, r) => (
                <Space>
                    <Tooltip title="Aperçu / Imprimer">
                        <Button size="small" shape="circle" icon={<PrinterOutlined />} onClick={() => apercu(r)} />
                    </Tooltip>
                    {/* Valider (préalable au paiement) — bordereau non payé et non encore validé. */}
                    {!isPaye(r) && !isValide(r) && peutValider && isSiege ? (
                        <Tooltip title="Valider le bordereau (préalable au paiement)">
                            <Button size="small" shape="circle" icon={<CheckCircleTwoTone twoToneColor="#1677ff" />}
                                onClick={() => alertValider(r)} />
                        </Tooltip>
                    ) : null}
                    {/* Recalculer les commissions (taux + retenue) tant que non payé. */}
                    {!isPaye(r) && peutRecalculer ? (
                        <Tooltip title="Recalculer les commissions (taux + retenue fiscale)">
                            <Button size="small" shape="circle" icon={<ReloadOutlined />}
                                onClick={() => alertRecalculer(r)} />
                        </Tooltip>
                    ) : null}
                    {/* Payer — uniquement une fois le bordereau VALIDÉ. */}
                    {!isPaye(r) && isValide(r) && isSiege ? (
                        <Tooltip title="Paiement de la commission">
                            <Button size="small" shape="circle" icon={<CreditCardOutlined />}
                                style={{ background: COLORS.warning, color: "#fff", borderColor: COLORS.warning }}
                                onClick={() => encaisserBordereau(r)} />
                        </Tooltip>
                    ) : null}
                    {isPaye(r) && LOGGED_USER?.agence?.typeAgence === "SIEGE" ? (
                        <Tooltip title="Modifier le mode de règlement">
                            <Button size="small" shape="circle" icon={<EditOutlined />}
                                onClick={() => modifierModePaiement(r)} />
                        </Tooltip>
                    ) : null}
                    {/* Modification exceptionnelle de la période affichée (HLA101, comme l'annulation). */}
                    {peutModifierPeriode ? (
                        <Tooltip title="Modifier la période affichée sur le bordereau">
                            <Button size="small" shape="circle" icon={<CalendarOutlined />}
                                onClick={() => ouvrirPeriode(r)} />
                        </Tooltip>
                    ) : null}
                    {/* Annuler le bordereau (HLA101) — uniquement s'il n'est PAS payé ; sinon bouton grisé
                        avec explication (le back refuse d'annuler un bordereau déjà payé). */}
                    {peutAnnuler ? (
                        <Tooltip title={isPaye(r)
                            ? "Annulation impossible : les commissions de ce bordereau ont déjà été payées"
                            : "Annuler le bordereau (désengage les commissions)"}>
                            <span style={{ display: "inline-block", cursor: isPaye(r) ? "not-allowed" : "pointer" }}>
                                <Button size="small" shape="circle" danger icon={<StopOutlined />}
                                    disabled={isPaye(r)}
                                    style={isPaye(r) ? { pointerEvents: "none" } : undefined}
                                    onClick={() => ouvrirAnnulation(r)} />
                            </span>
                        </Tooltip>
                    ) : null}
                </Space>
            ),
        },
    ];

    return (
        <div class="layout-wrapper layout-content-navbar">
            <div class="layout-container">
                <SideBar currentMenu={"COURTIERS"} />
                <div class="layout-page">
                    <NavBar />
                    <div class="content-wrapper">
                        <div class="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{
                                background: COLORS.gradient, borderRadius: 16, padding: "22px 26px",
                                marginBottom: 18, color: "#fff",
                                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
                            }}>
                                <div>
                                    <Title level={3} style={{ color: "#fff", margin: 0 }}>Bordereaux de commissions</Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Consultez, imprimez et réglez les bordereaux de commissions générés
                                    </Text>
                                </div>
                                <Space wrap>
                                    {peutRecalculer && (() => {
                                        const nbNonPayes = allDatas.filter((b) => !isPaye(b)).length;
                                        return (
                                            <Tooltip title="Recalculer les commissions + retenue de tous les bordereaux non payés (à re-valider ensuite)">
                                                <Button icon={<ReloadOutlined />} danger loading={recalculLotEnCours}
                                                    disabled={nbNonPayes === 0}
                                                    onClick={alertRecalculerLot}
                                                    style={{ background: "#fff", borderRadius: 8, fontWeight: 600 }}>
                                                    Recalculer les non-payés{nbNonPayes ? ` (${nbNonPayes})` : ""}
                                                </Button>
                                            </Tooltip>
                                        );
                                    })()}
                                    <Button type="primary" icon={<FileDoneOutlined />}
                                        onClick={() => setShowEtatModal(true)}
                                        style={{ background: "#fff", color: COLORS.primary, border: "none", borderRadius: 8, fontWeight: 600 }}>
                                        Éditer l'état
                                    </Button>
                                    <Tooltip title="Rafraîchir">
                                        <Button icon={<ReloadOutlined />} onClick={fetchData} />
                                    </Tooltip>
                                </Space>
                            </div>

                            {/* Indicateurs */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                                <Col xs={12} md={6}>
                                    <StatCard title="Bordereaux" subtitle="filtrés"
                                        value={`${filteredDatas.length}`} icon="bx bx-file"
                                        iconBg={COLORS.primary} gradient={COLORS.primary} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Montant total" subtitle="filtré"
                                        value={`${displayNumbers(Math.trunc(stats.montantTotal))} F`} icon="bx bx-wallet"
                                        iconBg={COLORS.success} gradient={COLORS.success} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <StatCard title="À valider" subtitle="générés"
                                        value={`${stats.nbAValider}`} icon="bx bx-time-five"
                                        iconBg={COLORS.warning} gradient={COLORS.warning} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <StatCard title="Validés" subtitle="à payer"
                                        value={`${stats.nbValides}`} icon="bx bx-check-shield"
                                        iconBg={COLORS.primary} gradient={COLORS.primary} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <StatCard title="Payés" subtitle="déjà payés"
                                        value={`${stats.nbPayes}`} icon="bx bx-check-circle"
                                        iconBg={COLORS.secondary} gradient={COLORS.secondary} />
                                </Col>
                            </Row>

                            {/* Filtres */}
                            <Card bordered={false} style={{ borderRadius: 14, marginBottom: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={9}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Recherche (apporteur / n° bordereau)</Text>
                                        <Input.Search allowClear placeholder="Nom de l'apporteur ou numéro de bordereau"
                                            value={search} onChange={(e) => setSearch(e.target.value)} />
                                    </Col>
                                    <Col xs={24} md={7}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Période (création)</Text>
                                        <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY"
                                            value={periode} onChange={setPeriode} allowClear={false} />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Statut</Text>
                                        <Select style={{ width: "100%" }} value={statutFilter} onChange={setStatutFilter}
                                            options={[
                                                { label: "Tous", value: "TOUS" },
                                                { label: "À valider", value: "A_VALIDER" },
                                                { label: "Validés (à payer)", value: "VALIDE" },
                                                { label: "Payés", value: "PAYE" },
                                                { label: "Non payés", value: "NON_PAYE" },
                                            ]} />
                                    </Col>
                                </Row>
                            </Card>

                            {/* Tableau */}
                            <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 8 }}>
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingData}>
                                        <Table
                                            columns={columns}
                                            dataSource={filteredDatas}
                                            rowKey="codeId"
                                            size="middle"
                                            locale={{ emptyText: <Empty description="Aucun bordereau de commission" /> }}
                                            pagination={{ showSizeChanger: true, defaultPageSize: 10, showTotal: (t) => `${t} bordereau(x)` }}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </Card>
                        </div>
                        <Footer />
                        <div class="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            {/* Modale aperçu / impression */}
            <Modal
                title={<Space><FileDoneOutlined style={{ color: COLORS.primary }} />Bordereau de commission N° {bordereauToPay?.numeroBordereau}</Space>}
                centered
                open={openModalBorderau}
                onCancel={() => setOpenModalBorderau(false)}
                width={1250}
                zIndex={2000}
                footer={[<Button key="close" type="primary" icon={<CheckCircleTwoTone />} onClick={() => setOpenModalBorderau(false)}>Fermer</Button>]}
            >
                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {bordereauToPay ? <PrintableBordereau bordereau={bordereauToPay} /> : null}
                </div>
            </Modal>

            {/* Modale « Éditer l'état des commissions » */}
            <Modal
                title={<Space><FileDoneOutlined style={{ color: COLORS.primary }} />État des bordereaux de commissions</Space>}
                centered
                open={showEtatModal}
                onCancel={() => setShowEtatModal(false)}
                width={1100}
                zIndex={2000}
                footer={[
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintEtat}>Imprimer</Button>,
                    <Button key="close" onClick={() => setShowEtatModal(false)}>Fermer</Button>,
                ]}
            >
                <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 12 }}>
                    <Col xs={24} md={10}>
                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Statut des commissions</Text>
                        <Select style={{ width: "100%" }} value={etatStatut} onChange={setEtatStatut}
                            options={[
                                { label: "Payées", value: "PAYE" },
                                { label: "Non payées", value: "NON_PAYE" },
                                { label: "Toutes", value: "TOUS" },
                            ]} />
                    </Col>
                    <Col xs={24} md={14}>
                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Période</Text>
                        <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY"
                            value={etatPeriode} onChange={setEtatPeriode}
                            placeholder={["Date début", "Date fin"]} />
                    </Col>
                </Row>
                <div style={{ maxHeight: "65vh", overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 8 }}>
                    <EtatCommissions ref={etatPrintRef} bordereaux={etatDatas}
                        statutLabel={etatStatutLabel} periodeLabel={etatPeriodeLabel} />
                </div>
            </Modal>

            {/* Modale de règlement / correction du mode de paiement */}
            <Modal
                title={editMode
                    ? `Modifier le mode de règlement — Bordereau N° ${bordereauToPay?.numeroBordereau || ""}`
                    : "Régler la commission — mode de paiement"}
                centered
                open={modalModePaiement}
                onOk={() => (editMode
                    ? alertModifierModePaiement(bordereauToPay)
                    : alertReglementBordereau(bordereauToPay))}
                onCancel={() => setModalModePaiement(false)}
                okText={editMode ? "Enregistrer la modification" : "Valider le règlement"}
            >
                <Form form={modePaiementForm} layout="vertical" name="modePaiementForm">
                    <Form.Item name="modePaiement" label="Mode de paiement" rules={[{ required: true }]}>
                        <Select onChange={(value) => setModePaiement(value)} placeholder="Sélectionnez le mode de paiement">
                            <Option value="ESPECES">ESPECES</Option>
                            <Option value="BANQUE">BANQUE (Chèque)</Option>
                            <Option value="MOBILE_MONEY">TMONEY / FLOOZ</Option>
                        </Select>
                    </Form.Item>
                    {modePaiement === "BANQUE" || modePaiement === "MOBILE_MONEY" ? (
                        <Form.Item name="numeroPaiement"
                            label={modePaiement === "BANQUE" ? "Numéro de chèque" : "Numéro FLOOZ / TMONEY"}
                            rules={[{ required: true, message: "Veuillez renseigner le champ !" }]}>
                            <Input
                                placeholder={modePaiement === "BANQUE" ? "Le numéro de chèque" : "Le numéro de téléphone MOOV (FLOOZ) / TOGOCOM (TMONEY)"}
                                onChange={(e) => setNumeroPaiement(e.target.value)} />
                        </Form.Item>
                    ) : null}
                    {modePaiement === "BANQUE" ? (
                        <Form.Item name="banque" label="Banque" rules={[{ required: true }]}>
                            <Select onChange={(value) => setBanque(value)} placeholder="Sélectionnez la banque">
                                {banques.map((b) => (
                                    <Option key={b.parametreId} value={b.libelleParametre}>{b.libelleParametre}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : null}
                    {!editMode && (
                        <Form.Item label="Observation / commentaire (facultatif)">
                            <Input.TextArea
                                rows={2}
                                value={commentaireEncaissement}
                                onChange={(e) => setCommentaireEncaissement(e.target.value)}
                                placeholder="Commentaire libre sur ce paiement" />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {/* Modification EXCEPTIONNELLE de la période affichée (HLA101). Purement documentaire :
                ne touche ni aux règlements ni aux montants. */}
            <Modal
                title={<span><CalendarOutlined style={{ marginRight: 8 }} />
                    Modifier la période — bordereau {bordereauPeriode?.numeroBordereau}</span>}
                open={modalPeriode}
                onCancel={() => setModalPeriode(false)}
                okText="Enregistrer la période"
                cancelText="Renoncer"
                okButtonProps={{ loading: periodeEnCours, disabled: !(periodeEdit && periodeEdit[0] && periodeEdit[1]) }}
                onOk={confirmerPeriode}
                destroyOnClose
            >
                <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
                    Cette période est <b>affichée telle quelle</b> sur le bordereau imprimé. La modifier
                    ne change ni les règlements liés ni les montants — opération réservée à l'habilitation
                    d'annulation (HLA101).
                </Typography.Paragraph>
                <Form layout="vertical">
                    <Form.Item label={<b>Période affichée (du … au …)</b>}>
                        <RangePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={periodeEdit && periodeEdit[0] ? [periodeEdit[0], periodeEdit[1]] : null}
                            onChange={(v) => setPeriodeEdit(v && v[0] ? [v[0], v[1]] : [null, null])}
                            allowClear
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Annulation d'un bordereau de commission NON payé (HLA101). Désengage les commissions
                liées (elles redeviennent disponibles) et passe le bordereau en annulé. */}
            <Modal
                title={<span><StopOutlined style={{ marginRight: 8, color: COLORS.danger || "#cf1322" }} />
                    Annuler le bordereau {bordereauAAnnuler?.numeroBordereau}</span>}
                open={modalAnnuler}
                onCancel={() => setModalAnnuler(false)}
                okText="Confirmer l'annulation"
                okType="danger"
                cancelText="Renoncer"
                okButtonProps={{ loading: annulerEnCours, disabled: !(motifAnnulation && motifAnnulation.trim()) }}
                onOk={confirmerAnnulation}
                destroyOnClose
            >
                <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
                    L'annulation <b>désengage les commissions</b> de ce bordereau : les règlements concernés
                    redeviennent disponibles pour un nouveau bordereau. Le bordereau est conservé (annulé),
                    avec son motif. Opération irréversible côté bordereau.
                </Typography.Paragraph>
                <Form layout="vertical">
                    <Form.Item label={<b>Motif d'annulation (obligatoire)</b>} required>
                        <Input.TextArea
                            rows={3}
                            value={motifAnnulation}
                            onChange={(e) => setMotifAnnulation(e.target.value)}
                            placeholder="Ex. : taux de commission erroné (à recréer après correction)"
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <div class="layout-overlay layout-menu-toggle"></div>
            <div class="drag-target"></div>
        </div>
    );
};

export default Bordereaux;
